import { pickCanonicalProjectSettings } from "~/lib/project-settings";
import type { AgentSidebarOpenDetail } from "~/lib/marketplace/site-agent-sidebar";
import {
  resolveCtaClustersForChannel,
  type HandoffCtaChannel,
} from "~/lib/team-handoff/cta-clusters-by-channel";

export type AgentResponsibility = {
  key: string;
  title: string;
  description: string;
  enabled: boolean;
};

type ChannelFlags = {
  aiChat?: boolean;
  talk?: boolean;
  contact?: boolean;
  call?: boolean;
  sms?: boolean;
  email?: boolean;
  whatsapp?: boolean;
  messenger?: boolean;
};

/** Responsibilities not yet available in production — hidden from agent profiles. */
export const UNAVAILABLE_RESPONSIBILITY_KEYS = new Set([
  "messenger",
  "whatsapp",
]);

export function isResponsibilityAvailable(key: string): boolean {
  return !UNAVAILABLE_RESPONSIBILITY_KEYS.has(key);
}

export function buildAgentResponsibilities(
  channels: ChannelFlags | null | undefined,
): AgentResponsibility[] {
  const liveChat = channels?.aiChat === true;
  const browserCall = channels?.talk === true;
  const contact = channels?.contact === true;
  const phone = channels?.call === true;
  const sms = channels?.sms === true;
  const email = channels?.email === true;
  const whatsapp = channels?.whatsapp === true;
  const messenger = channels?.messenger === true;

  return [
    {
      key: "aiChat",
      title: "Website Live Chat Agent",
      description:
        "Engage visitors in real time, answer questions, and guide them through next steps on your website.",
      enabled: liveChat,
    },
    {
      key: "contact",
      title: "Contact Form",
      description:
        "Capture visitor details through forms so your team can follow up by call, text, or email.",
      enabled: contact,
    },
    {
      key: "sms",
      title: "Text Message Support Agent",
      description:
        "Support visitors and patients by SMS for quick questions, updates, and follow-up communication.",
      enabled: sms,
    },
    {
      key: "email",
      title: "Email Support Agent",
      description:
        "Respond to inbound email, answer questions, and follow up with customers who prefer email communication.",
      enabled: email,
    },
    {
      key: "whatsapp",
      title: "WhatsApp Support Agent",
      description:
        "Handle conversations through WhatsApp for businesses that support this messaging channel.",
      enabled: whatsapp,
    },
    {
      key: "messenger",
      title: "Facebook Messenger Support Agent",
      description:
        "Respond to Facebook Messenger inquiries and keep social conversations moving forward.",
      enabled: messenger,
    },
    {
      key: "call",
      title: "Front Desk Receptionist",
      description:
        "Answer and route incoming phone calls while collecting details and supporting appointment workflows.",
      enabled: phone,
    },
    {
      key: "talk",
      title: "Website (Browser Call) Receptionist",
      description:
        "Enable website calls so website visitors can start a voice conversation with your Agent directly in the widget.",
      enabled: browserCall,
    },
  ].filter((item) => isResponsibilityAvailable(item.key));
}

const MARKETPLACE_RESPONSIBILITY_SHORT_LABELS: Record<string, string> = {
  aiChat: "Live Chat",
  contact: "Contact Form",
  sms: "Text Messages",
  email: "Email",
  whatsapp: "WhatsApp",
  messenger: "Messenger",
  call: "Phone Calls",
  talk: "Browser Calls",
  videoCalls: "Video Calls",
  welcomeVideo: "Welcome Video",
};

/** Settings-aligned responsibilities for marketplace agent profiles and cards. */
export function buildMarketplaceResponsibilities(
  channels: ChannelFlags | null | undefined,
  videoCallEnabled = false,
  welcomeVideoEnabled = false,
): AgentResponsibility[] {
  return [
    {
      key: "aiChat",
      title: "Live Chat",
      description:
        "Engage visitors in real time, answer questions, and guide them through next steps on your website.",
      enabled: channels?.aiChat === true,
    },
    {
      key: "call",
      title: "Phone Calls",
      description:
        "Answer and route incoming phone calls while collecting details and supporting appointment workflows.",
      enabled: channels?.call === true,
    },
    {
      key: "sms",
      title: "Text Messages",
      description:
        "Support visitors and patients by SMS for quick questions, updates, and follow-up communication.",
      enabled: channels?.sms === true,
    },
    {
      key: "email",
      title: "Email",
      description:
        "Respond to inbound email and follow up with customers who prefer email communication.",
      enabled: channels?.email === true,
    },
    {
      key: "talk",
      title: "Browser Calls",
      description:
        "Enable website voice calls so visitors can talk with your agent directly in the widget.",
      enabled: channels?.talk === true,
    },
    {
      key: "videoCalls",
      title: "Video Calls",
      description:
        "Enable video conversations so website visitors can connect face-to-face with your agent.",
      enabled: videoCallEnabled === true,
    },
    {
      key: "welcomeVideo",
      title: "Welcome Video",
      description:
        "Greet visitors with a welcome video and connect them with your team when they need help.",
      enabled: welcomeVideoEnabled === true,
    },
    {
      key: "whatsapp",
      title: "WhatsApp",
      description:
        "Handle conversations through WhatsApp for businesses that support this messaging channel.",
      enabled: channels?.whatsapp === true,
    },
    {
      key: "messenger",
      title: "Messenger",
      description:
        "Respond to Facebook Messenger inquiries and keep social conversations moving forward.",
      enabled: channels?.messenger === true,
    },
  ];
}

/** Short labels for marketplace cards and browse filters. */
export function getEnabledResponsibilityLabels(
  channels: ChannelFlags | null | undefined,
  _skills: string[] = [],
  videoCallEnabled = false,
  welcomeVideoEnabled = false,
): string[] {
  return buildMarketplaceResponsibilities(
    channels,
    videoCallEnabled,
    welcomeVideoEnabled,
  )
    .filter((r) => r.enabled)
    .map((r) => MARKETPLACE_RESPONSIBILITY_SHORT_LABELS[r.key] ?? r.title);
}

const MARKETPLACE_RESPONSIBILITY_WIDGET_TAB: Record<
  string,
  NonNullable<AgentSidebarOpenDetail["tab"]>
> = {
  aiChat: "aiChat",
  call: "call",
  sms: "sms",
  email: "email",
  talk: "talk",
  videoCalls: "videoCall",
  welcomeVideo: "welcomeVideo",
  whatsapp: "whatsapp",
  messenger: "messenger",
  contact: "contact",
};

export type MarketplaceLiveDemoAction = {
  key: string;
  label: string;
  tab: NonNullable<AgentSidebarOpenDetail["tab"]>;
};

/** Widget tabs for marketplace agent profile Live Demo buttons. */
export function getMarketplaceLiveDemoActions(
  channels: ChannelFlags | null | undefined,
  videoCallEnabled = false,
  welcomeVideoEnabled = false,
): MarketplaceLiveDemoAction[] {
  return buildMarketplaceResponsibilities(
    channels,
    videoCallEnabled,
    welcomeVideoEnabled,
  )
    .filter((r) => r.enabled)
    .map((r) => {
      const tab = MARKETPLACE_RESPONSIBILITY_WIDGET_TAB[r.key];
      if (!tab) return null;
      return {
        key: r.key,
        label: MARKETPLACE_RESPONSIBILITY_SHORT_LABELS[r.key] ?? r.title,
        tab,
      };
    })
    .filter((action): action is MarketplaceLiveDemoAction => action != null);
}

export function getProjectCtaQuestions(
  projectSettings:
    | Array<{
        ctaClusters?: Array<{ ctaQuestion?: string | null; channel?: string | null }> | null;
      }>
    | null
    | undefined,
  channel: HandoffCtaChannel = "chat",
): string[] {
  const canonical = pickCanonicalProjectSettings(projectSettings ?? []);
  const clusters = resolveCtaClustersForChannel(canonical?.ctaClusters ?? [], channel);
  return clusters
    .map((cluster) => cluster.ctaQuestion?.trim())
    .filter((question): question is string => !!question && question.length > 0);
}
